import React, { useEffect, useMemo, useState } from 'react';
import Combo from '../Combo';
import ExportButton from '../ExportButton';
import { EstadoCarga, EstadoError } from '../EstadoCaja';
import { colors } from '../../styles/tokens';
import { useEsMovil } from '../../hooks/useEsMovil';
import { IndicadoresService, statusDeError } from '../../services/indicadores.service';
import type { Store } from '../../data/stores';
import { storeChainBranch } from '../../data/stores';
import { computeMiTiendaOptions, firstStoreOfRegional, firstStoreOfSupervisor } from '../../utils/tienda';
import { exportXlsx, SheetSpec } from '../../utils/exportXlsx';
import { fmtPct } from '../../utils/format';
import {
  colorCumplimiento,
  colorVariacion,
  escribirScorecardEnUrl,
  fmtLugar,
  fmtValor,
  fmtVariacion,
  kpisDelBloque,
  movimiento,
  ORDEN_BLOQUES,
  ROTULO_BLOQUE,
  textoSemana,
  textoSemanaCorto,
} from '../../utils/scorecard';
import type { EstadoScorecard, KpiScorecard, RankingScorecard, ScorecardTienda } from '../../types/scorecard.types';
import TablaObjReal from './TablaObjReal';
import VistaRanking from './VistaRanking';

export interface VistaScorecardProps {
  /** Catálogo YA limitado al alcance de la cuenta. */
  stores: Store[];
  estado: EstadoScorecard;
  /** Tienda aplicada (compartida con la pestaña Tienda). */
  store: Store;
  onCambiarTienda: (storeId: string) => void;
  /** La semana elegida, para la pill del Header. */
  onSemanaChange?: (lunes: string) => void;
  /** Semana, subvista y vista iniciales (de la URL). */
  inicial?: { semana: string | null; sub: 'tienda' | 'ranking'; ver: 'semana' | 'acumulado' };
}

const ESPERA_MS = 300;

/**
 * PESTAÑA SCORECARD (spec `2026-10-16-scorecard-de-tienda.md` §6): el scorecard
 * semanal de una tienda —Resultados, Productividad, Cultura y Operación, con
 * objetivo, real, cumplimiento, año anterior, lugar y tendencia— y el ranking
 * de tiendas de su cadena o su regional.
 *
 * Todo sale ya calculado de la API (`/dashboard/scorecard`): aquí no se
 * recalcula ningún cociente. Elegir semana o tienda consulta al instante (no
 * hay nada que se teclee), con una espera de 300 ms para que tres clics
 * seguidos en ◀ sean UNA petición.
 */
const VistaScorecard: React.FC<VistaScorecardProps> = ({ stores, estado, store, onCambiarTienda, onSemanaChange, inicial }) => {
  const esMovil = useEsMovil();
  const semanas = estado.semanas.lista; // de la más reciente a la más vieja
  const [semana, setSemana] = useState<string>(() => {
    const pedida = inicial?.semana;
    return pedida && semanas.some((s) => s.lunes === pedida) ? pedida : estado.semanas.ultima ?? '';
  });
  const [sub, setSub] = useState<'tienda' | 'ranking'>(inicial?.sub ?? 'tienda');
  const [ver, setVer] = useState<'semana' | 'acumulado'>(inicial?.ver ?? 'semana');
  const [grupo, setGrupo] = useState<'cadena' | 'regional'>('cadena');

  const { chain, branchOffice } = storeChainBranch(store);

  useEffect(() => {
    if (semana) onSemanaChange?.(semana);
  }, [semana, onSemanaChange]);

  // URL compartible: lo que se ve es lo que se manda.
  useEffect(() => {
    escribirScorecardEnUrl({ semana, cadena: chain, tienda: branchOffice, sub, ver });
  }, [semana, chain, branchOffice, sub, ver]);

  // ── Scorecard de la tienda ──
  const [datos, setDatos] = useState<ScorecardTienda | null>(null);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState<{ mensaje: string; validacion: boolean } | null>(null);
  const [intento, setIntento] = useState(0);
  useEffect(() => {
    if (sub !== 'tienda' || !semana) return;
    let cancelado = false;
    setCargando(true);
    setError(null);
    const t = setTimeout(() => {
      IndicadoresService.getScorecard({ semana, chain, branchOffice, semanas: 52 })
        .then((r) => {
          if (!cancelado) setDatos(r);
        })
        .catch((e: unknown) => {
          if (cancelado) return;
          setDatos(null);
          setError({ mensaje: e instanceof Error ? e.message : 'No se pudo cargar el scorecard.', validacion: statusDeError(e) === 400 });
        })
        .finally(() => {
          if (!cancelado) setCargando(false);
        });
    }, ESPERA_MS);
    return () => {
      cancelado = true;
      clearTimeout(t);
    };
  }, [sub, semana, chain, branchOffice, intento]);

  // ── Ranking (sólo con la subvista abierta) ──
  const [ranking, setRanking] = useState<RankingScorecard | null>(null);
  const [cargandoRk, setCargandoRk] = useState(false);
  const [errorRk, setErrorRk] = useState<string | null>(null);
  const [intentoRk, setIntentoRk] = useState(0);
  useEffect(() => {
    if (sub !== 'ranking' || !semana) return;
    let cancelado = false;
    setCargandoRk(true);
    setErrorRk(null);
    const t = setTimeout(() => {
      IndicadoresService.getScorecardRanking({
        semana,
        grupo,
        chain: grupo === 'cadena' ? chain : undefined,
        regional: grupo === 'regional' ? store.regional : undefined,
      })
        .then((r) => {
          if (!cancelado) setRanking(r);
        })
        .catch((e: unknown) => {
          if (!cancelado) {
            setRanking(null);
            setErrorRk(e instanceof Error ? e.message : 'No se pudo cargar el ranking.');
          }
        })
        .finally(() => {
          if (!cancelado) setCargandoRk(false);
        });
    }, ESPERA_MS);
    return () => {
      cancelado = true;
      clearTimeout(t);
    };
  }, [sub, semana, grupo, chain, store.regional, intentoRk]);

  // ── Selector de semana ──
  const idx = semanas.findIndex((s) => s.lunes === semana);
  const anterior = idx >= 0 && idx < semanas.length - 1 ? semanas[idx + 1].lunes : null;
  const siguiente = idx > 0 ? semanas[idx - 1].lunes : null;
  const porAnio = useMemo(() => {
    const m = new Map<number, typeof semanas>();
    for (const s of semanas) {
      if (!m.has(s.anio)) m.set(s.anio, []);
      m.get(s.anio)!.push(s);
    }
    return Array.from(m.entries());
  }, [semanas]);

  const { miRegionalOpts, miSupervisorOpts, miTiendaOpts } = computeMiTiendaOptions(stores, store);

  // ── Excel ──
  const exportar = async () => {
    if (sub === 'ranking' && ranking) {
      const filas = ranking.filas.map((f) => [
        f.lugar?.lugar ?? '',
        f.lugar?.de ?? '',
        f.cadena,
        f.num,
        f.tienda,
        f.supervisor ?? '',
        f.puntaje ?? '',
        ...ORDEN_BLOQUES.map((b) => f.porBloque.find((x) => x.bloque === b)?.puntaje ?? ''),
        f.lugarAnterior ?? '',
        f.motivo ?? '',
      ]);
      await exportXlsx(
        [
          {
            name: 'Ranking',
            header: ['Lugar', 'De', 'Cadena', 'Tienda', 'Nombre', 'Supervisor', 'Puntaje', ...ORDEN_BLOQUES.map((b) => ROTULO_BLOQUE[b]), 'Lugar semana anterior', 'Motivo'],
            rows: filas,
          },
        ],
        `ranking-S${ranking.semana.numero}-${ranking.semana.anio}-${ranking.nombreGrupo.replace(/\s+/g, '_')}.xlsx`,
      );
      return;
    }
    if (!datos) return;
    await exportXlsx(hojasScorecard(datos), `scorecard-${datos.tienda.cadena}${datos.tienda.num}-S${datos.semana.numero}-${datos.semana.anio}.xlsx`);
  };

  const segBtn = (activo: boolean): React.CSSProperties => ({
    border: 'none',
    borderRadius: 999,
    padding: esMovil ? '10px 12px' : '6px 14px',
    minHeight: esMovil ? 44 : undefined,
    fontFamily: 'inherit',
    fontSize: 12.5,
    fontWeight: activo ? 800 : 700,
    cursor: 'pointer',
    background: activo ? colors.brand : 'transparent',
    color: activo ? '#fff' : colors.texto.secondary,
  });
  const grupoSeg: React.CSSProperties = {
    display: esMovil ? 'grid' : 'inline-flex',
    gridTemplateColumns: esMovil ? '1fr 1fr' : undefined,
    background: colors.switchBg,
    border: `1px solid ${colors.switchBorder}`,
    borderRadius: 999,
    padding: 3,
  };
  const flecha: React.CSSProperties = {
    border: `1px solid ${colors.border}`,
    background: colors.card,
    borderRadius: 10,
    minWidth: 44,
    minHeight: esMovil ? 44 : 36,
    fontSize: 16,
    fontWeight: 800,
    cursor: 'pointer',
    color: colors.texto.strong,
  };

  return (
    <div style={{ animation: 'cmfade .3s ease both' }} data-testid="vista-scorecard">
      {/* ===== Panel: ámbito (una tienda) y semana ===== */}
      <div
        data-testid="panel-scorecard"
        style={{
          display: 'grid',
          gridTemplateColumns: esMovil ? '1fr' : 'minmax(0, 3fr) minmax(0, 2fr)',
          gap: esMovil ? 14 : 18,
          marginTop: esMovil ? 12 : 16,
          background: colors.card,
          border: `1px solid ${colors.border}`,
          borderRadius: 16,
          padding: esMovil ? '13px 14px' : '13px 16px',
          position: 'relative',
          zIndex: 20,
        }}
      >
        <div style={{ display: 'grid', gridTemplateColumns: esMovil ? '1fr' : 'repeat(3, minmax(0, 1fr))', gap: esMovil ? 12 : 10, minWidth: 0 }}>
          <Combo
            label="Regional"
            value={store.regional}
            options={miRegionalOpts}
            onChange={(v) => {
              const s = firstStoreOfRegional(stores, v);
              if (s) onCambiarTienda(s.id);
            }}
            accent
            flat={!esMovil}
          />
          <Combo
            label="Supervisor"
            value={store.supervisor}
            options={miSupervisorOpts}
            onChange={(v) => {
              const s = firstStoreOfSupervisor(stores, store.regional, v);
              if (s) onCambiarTienda(s.id);
            }}
            accent
            flat={!esMovil}
          />
          <Combo label="Tienda" value={store.id} options={miTiendaOpts} onChange={onCambiarTienda} flat={!esMovil} />
        </div>
        <div style={{ minWidth: 0 }}>
          <label htmlFor="scorecard-semana" style={{ fontSize: 10, letterSpacing: '.09em', textTransform: 'uppercase', color: '#9298a3', fontWeight: 800 }}>
            Semana (lunes a domingo)
          </label>
          <div style={{ display: 'flex', gap: 6, marginTop: 4, alignItems: 'stretch' }}>
            <button type="button" aria-label="Semana anterior" disabled={!anterior} onClick={() => anterior && setSemana(anterior)} style={{ ...flecha, opacity: anterior ? 1 : 0.4 }}>
              ◀
            </button>
            <select
              id="scorecard-semana"
              data-testid="scorecard-semana"
              value={semana}
              onChange={(e) => setSemana(e.target.value)}
              style={{
                flex: 1,
                minWidth: 0,
                minHeight: esMovil ? 44 : 36,
                border: `1px solid ${colors.border}`,
                borderRadius: 10,
                padding: '0 10px',
                fontFamily: 'inherit',
                fontWeight: 700,
                fontSize: 13.5,
                background: colors.card,
                color: colors.texto.primary,
              }}
            >
              {porAnio.map(([anio, lista]) => (
                <optgroup key={anio} label={String(anio)}>
                  {lista.map((s) => (
                    <option key={s.lunes} value={s.lunes}>
                      {esMovil ? textoSemanaCorto(s.lunes) : textoSemana(s.lunes)}
                      {s.estado === 'provisional' && !esMovil ? ' (provisional)' : ''}
                    </option>
                  ))}
                </optgroup>
              ))}
            </select>
            <button type="button" aria-label="Semana siguiente" disabled={!siguiente} onClick={() => siguiente && setSemana(siguiente)} style={{ ...flecha, opacity: siguiente ? 1 : 0.4 }}>
              ▶
            </button>
          </div>
          {estado.semanas.ultima && semana !== estado.semanas.ultima && (
            <button
              type="button"
              onClick={() => setSemana(estado.semanas.ultima as string)}
              style={{ marginTop: 6, background: 'none', border: 'none', padding: 0, color: colors.brand, fontWeight: 800, fontSize: 12.5, cursor: 'pointer', fontFamily: 'inherit' }}
            >
              Última semana cerrada
            </button>
          )}
        </div>
      </div>
      <span aria-live="polite" style={{ position: 'absolute', left: -9999 }}>
        {semana ? `Mostrando ${textoSemana(semana)}` : ''}
      </span>

      {/* ===== Barra: subvista, vista y Excel ===== */}
      <div style={{ display: 'flex', flexDirection: esMovil ? 'column' : 'row', gap: 10, alignItems: esMovil ? 'stretch' : 'center', marginTop: 14 }}>
        <div role="group" aria-label="Qué ver" style={grupoSeg}>
          <button type="button" aria-pressed={sub === 'tienda'} onClick={() => setSub('tienda')} style={segBtn(sub === 'tienda')}>
            Scorecard de la tienda
          </button>
          <button type="button" aria-pressed={sub === 'ranking'} onClick={() => setSub('ranking')} style={segBtn(sub === 'ranking')}>
            Ranking de tiendas
          </button>
        </div>
        <div style={{ flex: 1 }} />
        {sub === 'tienda' ? (
          <div role="group" aria-label="Periodo de las cifras" style={grupoSeg}>
            <button type="button" aria-pressed={ver === 'semana'} onClick={() => setVer('semana')} style={segBtn(ver === 'semana')}>
              Semana {datos ? datos.semana.numero : ''}
            </button>
            <button type="button" aria-pressed={ver === 'acumulado'} onClick={() => setVer('acumulado')} style={segBtn(ver === 'acumulado')}>
              Acumulado {datos ? `${datos.semana.anio} (S1–S${datos.semana.numero})` : 'del año'}
            </button>
          </div>
        ) : (
          <div role="group" aria-label="Comparar dentro de" style={grupoSeg}>
            <button type="button" aria-pressed={grupo === 'cadena'} onClick={() => setGrupo('cadena')} style={segBtn(grupo === 'cadena')}>
              Cadena {store.cadena}
            </button>
            <button type="button" aria-pressed={grupo === 'regional'} onClick={() => setGrupo('regional')} style={segBtn(grupo === 'regional')}>
              Regional {store.regional}
            </button>
          </div>
        )}
        <ExportButton onExport={exportar} disabled={sub === 'tienda' ? !datos : !ranking} testid="scorecard" />
      </div>

      {sub === 'ranking' && (
        <div style={tarjeta(esMovil)}>
          {cargandoRk && !ranking && <EstadoCarga testid="ranking-cargando" texto="Cargando el ranking…" marginTop={0} />}
          {errorRk && <EstadoError testid="ranking-error" titulo="No se pudo cargar el ranking" mensaje={errorRk} onReintentar={() => setIntentoRk((n) => n + 1)} marginTop={0} />}
          {ranking && (
            <div style={{ opacity: cargandoRk ? 0.5 : 1 }} aria-busy={cargandoRk}>
              <VistaRanking
                ranking={ranking}
                llaveActual={`${chain}|${branchOffice}`}
                onAbrirTienda={(cadena, num) => {
                  const s = stores.find((x) => storeChainBranch(x).chain === cadena && Number(x.num) === num);
                  if (s) onCambiarTienda(s.id);
                  setSub('tienda');
                }}
              />
            </div>
          )}
        </div>
      )}

      {sub === 'tienda' && (
        <>
          {cargando && !datos && <EstadoCarga testid="scorecard-cargando" texto="Cargando el scorecard…" />}
          {error && (
            <EstadoError
              testid="scorecard-error"
              titulo="No se pudo cargar el scorecard"
              mensaje={error.mensaje}
              validacion={error.validacion}
              onReintentar={() => setIntento((n) => n + 1)}
            />
          )}
          {datos && <Contenido datos={datos} ver={ver} cargando={cargando} />}
        </>
      )}
    </div>
  );
};

const tarjeta = (esMovil: boolean): React.CSSProperties => ({
  marginTop: 14,
  background: colors.card,
  border: `1px solid ${colors.border}`,
  borderRadius: 16,
  padding: esMovil ? '13px 14px' : '16px 18px',
  minWidth: 0,
});

/** El scorecard publicado de la tienda: título, fuentes, resumen, bloques y ranking. */
const Contenido: React.FC<{ datos: ScorecardTienda; ver: 'semana' | 'acumulado'; cargando: boolean }> = ({ datos, ver, cargando }) => {
  const esMovil = useEsMovil();
  const k = (clave: string) => datos.kpis.find((x) => x.clave === clave);
  const rotulo = (clave: string) => k(clave)?.rotulo ?? clave;
  const mov = movimiento(datos.ranking.cadena?.lugar, datos.ranking.cadena?.anterior);

  return (
    <div style={{ opacity: cargando ? 0.5 : 1, transition: 'opacity .15s' }} aria-busy={cargando} data-testid="scorecard-contenido">
      {/* Título */}
      <div data-testid="scorecard-titulo" style={{ display: 'flex', alignItems: 'baseline', gap: esMovil ? 8 : 12, flexWrap: 'wrap', marginTop: 16 }}>
        <h2 style={{ margin: 0, fontSize: esMovil ? 22 : 27, fontWeight: 800, letterSpacing: '-.02em' }}>{datos.tienda.nombre}</h2>
        <span style={{ fontSize: 11.5, fontWeight: 800, color: colors.brand, background: colors.brandSoftBg, border: `1px solid ${colors.brandSoftBorder}`, borderRadius: 8, padding: '4px 10px' }}>
          {datos.tienda.cadena} {datos.tienda.num}
        </span>
        <span style={{ fontSize: 12.5, color: colors.texto.secondary, fontWeight: 600 }}>
          Supervisor <strong style={{ color: colors.texto.strong }}>{datos.tienda.supervisor ?? '—'}</strong> · Regional{' '}
          <strong style={{ color: colors.texto.strong }}>{datos.tienda.regional ?? '—'}</strong> · {datos.semana.etiqueta}
          {datos.semana.estado === 'provisional' ? ' (provisional)' : ''}
        </span>
      </div>

      {/* Fuentes: un chip por fuente, con texto (nunca sólo color) */}
      <div data-testid="scorecard-fuentes" style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 10 }}>
        {datos.calidad.fuentes.map((f) => {
          const ok = f.estado === 'completa';
          const pendiente = f.estado === 'no_conectada';
          return (
            <span
              key={f.clave}
              title={f.nota ?? undefined}
              style={{
                fontSize: 11.5,
                fontWeight: 700,
                borderRadius: 999,
                padding: '3px 10px',
                border: `1px solid ${ok ? '#bfe9cf' : pendiente ? colors.border : '#fde68a'}`,
                background: ok ? '#eafaf0' : pendiente ? colors.chipBg : '#fef3c7',
                color: ok ? '#0f7a3b' : pendiente ? colors.texto.tertiary : '#92400e',
              }}
            >
              {ok ? '✓' : pendiente ? '○' : '!'} {f.rotulo}
              {!ok ? ` · ${f.estado === 'no_conectada' ? 'pendiente' : f.estado === 'parcial' ? 'parcial' : 'sin dato'}` : ''}
            </span>
          );
        })}
      </div>
      {datos.calidad.avisos.length > 0 && (
        <ul style={{ margin: '8px 0 0', paddingLeft: 18, fontSize: 12.5, color: '#92400e' }} data-testid="scorecard-avisos">
          {datos.calidad.avisos.map((a) => (
            <li key={a}>{a}</li>
          ))}
        </ul>
      )}

      {/* Resumen: 4 mosaicos fijos (cero huecos en cualquier ancho) */}
      <div style={{ display: 'grid', gridTemplateColumns: esMovil ? 'repeat(2, minmax(0, 1fr))' : 'repeat(4, minmax(0, 1fr))', gap: 12, marginTop: 14 }}>
        {(['venta_neta', 'pares', 'ticket_promedio'] as const).map((clave) => {
          const kpi = k(clave);
          return kpi ? <Mosaico key={clave} kpi={kpi} ver={ver} /> : null;
        })}
        <div style={mosaico} data-testid="mosaico-lugar">
          <div style={mosaicoTitulo}>Lugar general</div>
          <div style={{ fontSize: 24, fontWeight: 800 }}>
            {datos.ranking.cadena ? `#${datos.ranking.cadena.lugar}` : '—'}
            {mov.texto && <span style={{ fontSize: 13, marginLeft: 8, color: mov.color }}>{mov.texto}</span>}
          </div>
          <div style={mosaicoPie}>
            {datos.ranking.cadena ? `de ${datos.ranking.cadena.de} en ${datos.tienda.cadena}` : datos.ranking.motivo ?? 'Sin lugar esta semana'}
            {datos.ranking.regional ? ` · ${fmtLugar(datos.ranking.regional)} en la regional` : ''}
          </div>
          <div style={mosaicoPie}>{datos.ranking.puntaje !== null ? `${datos.ranking.puntaje.toLocaleString('es-MX', { maximumFractionDigits: 1 })} pts` : ''}</div>
        </div>
      </div>

      {/* Bloques */}
      {ORDEN_BLOQUES.map((bloque) => {
        const kpis = kpisDelBloque(datos.kpis, bloque);
        if (!kpis.length) return null;
        const conDato = kpis.some((x) => x[ver].real !== null);
        return (
          <section key={bloque} style={tarjeta(esMovil)} aria-label={ROTULO_BLOQUE[bloque]} data-testid={`bloque-${bloque}`}>
            <h3 style={{ margin: '0 0 10px', fontSize: 15, fontWeight: 800, letterSpacing: '.02em' }}>{ROTULO_BLOQUE[bloque]}</h3>
            {!conDato && (
              <p style={{ margin: '0 0 8px', fontSize: 12.5, color: colors.texto.tertiary }}>
                Este bloque todavía no tiene datos esta semana: su fuente se conecta en una fase siguiente o está en validación.
              </p>
            )}
            <TablaObjReal titulo={ROTULO_BLOQUE[bloque]} kpis={kpis} ver={ver} />
            {bloque === 'cultura' && datos.plantilla.length > 0 && (
              <table style={{ marginTop: 12, borderCollapse: 'collapse', fontSize: 13 }} data-testid="plantilla-puestos">
                <caption style={{ textAlign: 'left', fontWeight: 800, paddingBottom: 6 }}>Plantilla por puesto</caption>
                <thead>
                  <tr>
                    <th scope="col" style={{ textAlign: 'left', padding: '4px 12px 4px 0' }}>Puesto</th>
                    <th scope="col" style={{ textAlign: 'right', padding: '4px 0' }}>Activos</th>
                  </tr>
                </thead>
                <tbody>
                  {datos.plantilla.map((p) => (
                    <tr key={p.puesto}>
                      <td style={{ padding: '3px 12px 3px 0' }}>{p.puesto}</td>
                      <td style={{ textAlign: 'right', fontWeight: 800 }}>{p.activos}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </section>
        );
      })}

      {/* Ranking de la tienda */}
      <section style={tarjeta(esMovil)} aria-label="Ranking" data-testid="tarjeta-ranking">
        <h3 style={{ margin: '0 0 10px', fontSize: 15, fontWeight: 800 }}>Ranking</h3>
        <div style={{ display: 'grid', gridTemplateColumns: esMovil ? '1fr' : 'minmax(0, 1fr) minmax(0, 1fr)', gap: 16 }}>
          <div>
            {datos.ranking.porBloque.map((b) => (
              <div key={b.bloque} style={{ marginBottom: 8 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12.5, fontWeight: 700 }}>
                  <span>
                    {b.rotulo} <span style={{ color: colors.texto.quaternary, fontWeight: 600 }}>· peso {b.peso}</span>
                  </span>
                  <span>{b.puntaje === null ? '—' : `${b.puntaje.toLocaleString('es-MX', { maximumFractionDigits: 1 })} pts`}</span>
                </div>
                <div style={{ height: 8, background: colors.ringTrack, borderRadius: 999, overflow: 'hidden', marginTop: 3 }}>
                  <div style={{ width: `${Math.max(0, Math.min(100, b.puntaje ?? 0))}%`, height: '100%', background: colors.brand }} />
                </div>
              </div>
            ))}
            <p style={{ fontSize: 11.5, color: colors.texto.tertiary, margin: '6px 0 0' }}>
              ¿Cómo se calcula? Cada indicador da un percentil dentro de la cadena (1 = la mejor); el puntaje es su promedio
              ponderado por el peso. Los indicadores en validación o sin dato para ninguna tienda no cuentan.
            </p>
          </div>
          <div style={{ fontSize: 13 }}>
            <div style={{ fontWeight: 800, color: colors.verde }}>Dónde suma</div>
            <ul style={{ margin: '4px 0 10px', paddingLeft: 18 }}>
              {datos.ranking.dondeSuma.length ? datos.ranking.dondeSuma.map((c) => <li key={c}>{rotulo(c)}</li>) : <li>—</li>}
            </ul>
            <div style={{ fontWeight: 800, color: colors.rojo }}>Dónde resta</div>
            <ul style={{ margin: '4px 0 0', paddingLeft: 18 }}>
              {datos.ranking.dondeResta.length ? datos.ranking.dondeResta.map((c) => <li key={c}>{rotulo(c)}</li>) : <li>—</li>}
            </ul>
          </div>
        </div>
      </section>
    </div>
  );
};

const mosaico: React.CSSProperties = {
  background: colors.card,
  border: `1px solid ${colors.border}`,
  borderRadius: 14,
  padding: '12px 14px',
  minWidth: 0,
  overflowWrap: 'anywhere',
};
const mosaicoTitulo: React.CSSProperties = {
  fontSize: 10.5,
  letterSpacing: '.08em',
  textTransform: 'uppercase',
  color: colors.texto.quaternary,
  fontWeight: 800,
  marginBottom: 4,
};
const mosaicoPie: React.CSSProperties = { fontSize: 12, color: colors.texto.secondary, marginTop: 2 };

const Mosaico: React.FC<{ kpi: KpiScorecard; ver: 'semana' | 'acumulado' }> = ({ kpi, ver }) => {
  const m = kpi[ver];
  const gris = kpi.estado !== 'ok';
  return (
    <div style={mosaico} data-testid={`mosaico-${kpi.clave}`}>
      <div style={mosaicoTitulo}>{kpi.rotulo}</div>
      <div style={{ fontSize: 22, fontWeight: 800 }}>{fmtValor(m.real, kpi.unidad)}</div>
      {m.cumplimiento !== null && (
        <div style={{ ...mosaicoPie, fontWeight: 800, color: gris ? colors.texto.tertiary : colorCumplimiento(m.cumplimiento, kpi.polaridad) }}>
          Cumplimiento {fmtPct(m.cumplimiento)}
        </div>
      )}
      <div style={{ ...mosaicoPie, color: gris ? colors.texto.tertiary : colorVariacion(m.variacionAA, kpi.polaridad) }}>
        {m.variacionAA !== null ? `${fmtVariacion(m.variacionAA)} vs año anterior` : 'Sin comparativo del año anterior'}
      </div>
    </div>
  );
};

/** Hojas del Excel de una tienda: un bloque por hoja, semana y acumulado lado a lado. */
export function hojasScorecard(d: ScorecardTienda): SheetSpec[] {
  const num = (v: number | null) => (v === null ? '' : v);
  const header = [
    'Indicador',
    'Unidad',
    'Objetivo (semana)',
    'Real (semana)',
    'Cumplimiento (semana)',
    'Año anterior (semana)',
    'Vs AA (semana)',
    'Lugar en cadena',
    'De',
    'Objetivo (acumulado)',
    'Real (acumulado)',
    'Cumplimiento (acumulado)',
    'Año anterior (acumulado)',
    'Vs AA (acumulado)',
    'Estado',
    'Motivo',
  ];
  const hojas: SheetSpec[] = ORDEN_BLOQUES.map((b) => ({
    name: ROTULO_BLOQUE[b],
    header,
    rows: kpisDelBloque(d.kpis, b).map((k) => [
      k.rotulo,
      k.unidad,
      num(k.semana.objetivo),
      num(k.semana.real),
      num(k.semana.cumplimiento),
      num(k.semana.anterior),
      num(k.semana.variacionAA),
      k.lugar.cadena?.lugar ?? '',
      k.lugar.cadena?.de ?? '',
      num(k.acumulado.objetivo),
      num(k.acumulado.real),
      num(k.acumulado.cumplimiento),
      num(k.acumulado.anterior),
      num(k.acumulado.variacionAA),
      k.estado,
      k.motivo ?? '',
    ]),
  }));
  hojas.push({
    name: 'Tendencias',
    header: ['Semana', 'Lunes', ...d.kpis.flatMap((k) => [`${k.rotulo} (actual)`, `${k.rotulo} (año anterior)`])],
    rows: (d.kpis[0]?.tendencia ?? []).map((t, i) => [
      t.numero,
      t.lunes,
      ...d.kpis.flatMap((k) => [num(k.tendencia[i]?.actual ?? null), num(k.tendencia[i]?.anterior ?? null)]),
    ]),
  });
  hojas.push({
    name: 'Fuentes',
    header: ['Fuente', 'Estado', 'Nota'],
    rows: [
      ...d.calidad.fuentes.map((f) => [f.rotulo, f.estado, f.nota ?? '']),
      ...d.calidad.avisos.map((a) => ['Aviso', '', a]),
      ['Semana', d.semana.estado, `${d.semana.etiqueta} · catálogo ${d.semana.catalogoVersion} · pesos ${d.semana.pesosVersion}`],
    ],
  });
  return hojas;
}

export default VistaScorecard;
